/**
 * Smart Message block schema (bodyVersion 2) — the Block[] discriminated union
 * that replaces the legacy string body. Spec: knowledge/wiki/features/messages-d6-parity-spec.md B1.
 *
 * Three groups:
 *   Content   — sender writes it, nothing comes back (text, attachment, location)
 *   Questions — each recipient answers, answers roll up into stats
 *               (options, shortText, acknowledge, signature, upload, transport)
 *   Action    — an actionable extra (calendar)
 *
 * MIRRORED FILES — keep all three in sync when editing:
 *   src/models/smartMessageBlocks.js        (web, ESM — this file)
 *   functions/services/smartMessageBlocks.js (Cloud Functions, CJS — validation authority)
 *   mobile/src/models/smartMessageBlocks.js  (mobile, ESM)
 */

export const BLOCK_TYPES = {
  TEXT: 'text',
  ATTACHMENT: 'attachment',
  OPTIONS: 'options',
  SHORT_TEXT: 'shortText',
  ACKNOWLEDGE: 'acknowledge',
  SIGNATURE: 'signature',
  UPLOAD: 'upload',
  TRANSPORT: 'transport',
  CALENDAR: 'calendar',
  LOCATION: 'location',
};

/** Blocks that capture a per-recipient response (get a blockResponseCounts entry). */
export const RESPONSE_BLOCK_TYPES = [
  BLOCK_TYPES.OPTIONS,
  BLOCK_TYPES.SHORT_TEXT,
  BLOCK_TYPES.ACKNOWLEDGE,
  BLOCK_TYPES.SIGNATURE,
  BLOCK_TYPES.UPLOAD,
  BLOCK_TYPES.TRANSPORT,
  BLOCK_TYPES.CALENDAR,
];

/**
 * Blocks that may carry `required` and count toward recipient status
 * (complete = all required answered). Calendar is deliberately excluded:
 * its response is recorded but never gates completion.
 */
export const REQUIRABLE_BLOCK_TYPES = [
  BLOCK_TYPES.OPTIONS,
  BLOCK_TYPES.SHORT_TEXT,
  BLOCK_TYPES.ACKNOWLEDGE,
  BLOCK_TYPES.SIGNATURE,
  BLOCK_TYPES.UPLOAD,
  BLOCK_TYPES.TRANSPORT,
];

/** Blocks whose answers are countable into a byValue distribution. */
export const COUNTABLE_BLOCK_TYPES = [
  BLOCK_TYPES.OPTIONS,
  BLOCK_TYPES.ACKNOWLEDGE,
  BLOCK_TYPES.TRANSPORT,
];

/**
 * Extra detail a transport arrangement can demand before it counts as an
 * answer. "Another adult will collect them" is worthless without a name;
 * "another arrangement" is worthless without a description.
 */
export const TRANSPORT_FOLLOW_UPS = ['person', 'detail'];

export function isBlockBody(body) {
  return Array.isArray(body);
}

export function isResponseBlock(block) {
  return !!block && RESPONSE_BLOCK_TYPES.includes(block.type);
}

export function isRequiredBlock(block) {
  return !!block && block.required === true &&
    REQUIRABLE_BLOCK_TYPES.includes(block.type);
}

/** All blocks that capture responses. */
export function getResponseBlocks(body) {
  return isBlockBody(body) ? body.filter(isResponseBlock) : [];
}

/** All blocks that must be answered for a recipient to be `complete`. */
export function getRequiredBlocks(body) {
  return isBlockBody(body) ? body.filter(isRequiredBlock) : [];
}

const isNonEmptyString = (v) => typeof v === 'string' && v.trim().length > 0;

// Guardrails so an authored message can't blow past Firestore's 1 MB doc limit
// mid-fan-out (a partial commit would leave a half-sent message). Generous
// ceilings — they reject only absurd input, never a real permission slip.
const BLOCK_LIMITS = {
  maxBlocks: 50,
  maxTextLength: 5000,
  maxLabelLength: 500,
  maxOptions: 20,
  maxOptionLabelLength: 200,
  maxAttachmentFiles: 20,
};

/**
 * Validate a Block[] body. Returns an array of human-readable error strings —
 * empty when valid. Never throws.
 *
 * @param {*} body
 * @returns {string[]}
 */
export function validateBlocks(body) {
  const errors = [];
  if (!Array.isArray(body)) return ['body must be an array of blocks'];
  if (body.length === 0) return ['body must contain at least one block'];
  if (body.length > BLOCK_LIMITS.maxBlocks) {
    return [`body has too many blocks (max ${BLOCK_LIMITS.maxBlocks})`];
  }

  const seenIds = new Set();
  body.forEach((block, i) => {
    const at = `block ${i + 1}`;
    if (!block || typeof block !== 'object') {
      errors.push(`${at}: must be an object`);
      return;
    }
    if (!isNonEmptyString(block.id)) {
      errors.push(`${at}: id is required`);
    } else if (seenIds.has(block.id)) {
      errors.push(`${at}: duplicate block id "${block.id}"`);
    } else {
      seenIds.add(block.id);
    }
    if (!Object.values(BLOCK_TYPES).includes(block.type)) {
      errors.push(`${at}: unknown type "${block.type}"`);
      return;
    }

    switch (block.type) {
      case BLOCK_TYPES.TEXT:
        if (!isNonEmptyString(block.text)) {
          errors.push(`${at} (text): text is required`);
        } else if (block.text.length > BLOCK_LIMITS.maxTextLength) {
          errors.push(`${at} (text): text is too long (max ${BLOCK_LIMITS.maxTextLength} characters)`);
        }
        break;

      case BLOCK_TYPES.ATTACHMENT:
        if (!Array.isArray(block.files) || block.files.length === 0) {
          errors.push(`${at} (attachment): files must be a non-empty array`);
        } else if (block.files.length > BLOCK_LIMITS.maxAttachmentFiles) {
          errors.push(`${at} (attachment): too many files (max ${BLOCK_LIMITS.maxAttachmentFiles})`);
        } else {
          block.files.forEach((f, j) => {
            if (!f || !isNonEmptyString(f.url) || !isNonEmptyString(f.name)) {
              errors.push(`${at} (attachment): file ${j + 1} needs url and name`);
            }
          });
        }
        break;

      case BLOCK_TYPES.OPTIONS: {
        if (!isNonEmptyString(block.label)) {
          errors.push(`${at} (options): label is required`);
        } else if (block.label.length > BLOCK_LIMITS.maxLabelLength) {
          errors.push(`${at} (options): label is too long (max ${BLOCK_LIMITS.maxLabelLength} characters)`);
        }
        if (!Array.isArray(block.options) || block.options.length < 2) {
          errors.push(`${at} (options): at least two options are required`);
        } else if (block.options.length > BLOCK_LIMITS.maxOptions) {
          errors.push(`${at} (options): too many options (max ${BLOCK_LIMITS.maxOptions})`);
        } else {
          const optIds = new Set();
          block.options.forEach((opt, j) => {
            if (!opt || !isNonEmptyString(opt.id) || !isNonEmptyString(opt.label)) {
              errors.push(`${at} (options): option ${j + 1} needs id and label`);
            } else if (opt.label.length > BLOCK_LIMITS.maxOptionLabelLength) {
              errors.push(`${at} (options): option ${j + 1} label is too long (max ${BLOCK_LIMITS.maxOptionLabelLength} characters)`);
            } else if (optIds.has(opt.id)) {
              errors.push(`${at} (options): duplicate option id "${opt.id}"`);
            } else {
              optIds.add(opt.id);
            }
          });
        }
        break;
      }

      case BLOCK_TYPES.SHORT_TEXT:
      case BLOCK_TYPES.ACKNOWLEDGE:
      case BLOCK_TYPES.SIGNATURE:
      case BLOCK_TYPES.UPLOAD:
        if (!isNonEmptyString(block.label)) {
          errors.push(`${at} (${block.type}): label is required`);
        } else if (block.label.length > BLOCK_LIMITS.maxLabelLength) {
          errors.push(`${at} (${block.type}): label is too long (max ${BLOCK_LIMITS.maxLabelLength} characters)`);
        }
        break;

      case BLOCK_TYPES.TRANSPORT: {
        if (!isNonEmptyString(block.label)) {
          errors.push(`${at} (transport): label is required`);
        } else if (block.label.length > BLOCK_LIMITS.maxLabelLength) {
          errors.push(`${at} (transport): label is too long (max ${BLOCK_LIMITS.maxLabelLength} characters)`);
        }
        if (!Array.isArray(block.options) || block.options.length < 2) {
          errors.push(`${at} (transport): at least two arrangements are required`);
        } else if (block.options.length > BLOCK_LIMITS.maxOptions) {
          errors.push(`${at} (transport): too many arrangements (max ${BLOCK_LIMITS.maxOptions})`);
        } else {
          const optIds = new Set();
          block.options.forEach((opt, j) => {
            if (!opt || !isNonEmptyString(opt.id) || !isNonEmptyString(opt.label)) {
              errors.push(`${at} (transport): arrangement ${j + 1} needs id and label`);
              return;
            }
            if (opt.label.length > BLOCK_LIMITS.maxOptionLabelLength) {
              errors.push(`${at} (transport): arrangement ${j + 1} label is too long (max ${BLOCK_LIMITS.maxOptionLabelLength} characters)`);
            }
            if (optIds.has(opt.id)) {
              errors.push(`${at} (transport): duplicate arrangement id "${opt.id}"`);
            } else {
              optIds.add(opt.id);
            }
            if (opt.requires != null && !TRANSPORT_FOLLOW_UPS.includes(opt.requires)) {
              errors.push(`${at} (transport): arrangement ${j + 1} has an unknown follow-up "${opt.requires}"`);
            }
          });
        }
        break;
      }

      case BLOCK_TYPES.CALENDAR: {
        const ev = block.event;
        if (!ev || typeof ev !== 'object') {
          errors.push(`${at} (calendar): event is required`);
        } else {
          if (!isNonEmptyString(ev.title)) errors.push(`${at} (calendar): event.title is required`);
          if (ev.startsAt == null) errors.push(`${at} (calendar): event.startsAt is required`);
        }
        break;
      }

      case BLOCK_TYPES.LOCATION: {
        const place = block.place;
        if (!place || typeof place !== 'object') {
          errors.push(`${at} (location): place is required`);
        } else {
          if (!isNonEmptyString(place.name) && !isNonEmptyString(place.address)) {
            errors.push(`${at} (location): place needs a name or address`);
          }
          if (place.lat != null && typeof place.lat !== 'number') {
            errors.push(`${at} (location): place.lat must be a number`);
          }
          if (place.lng != null && typeof place.lng !== 'number') {
            errors.push(`${at} (location): place.lng must be a number`);
          }
        }
        break;
      }

      default:
        break;
    }
  });

  return errors;
}

/**
 * Merge adjacent text blocks into one (keeps the first block's id) and drop
 * text blocks that ended up empty. Composers call this on save so a message
 * never fragments into consecutive text runs.
 *
 * @param {Array} body
 * @returns {Array} new array; input untouched
 */
export function mergeAdjacentTextBlocks(body) {
  if (!isBlockBody(body)) return body;
  const merged = [];
  for (const block of body) {
    if (block?.type === BLOCK_TYPES.TEXT && !isNonEmptyString(block.text)) continue;
    const prev = merged[merged.length - 1];
    if (block?.type === BLOCK_TYPES.TEXT && prev?.type === BLOCK_TYPES.TEXT) {
      merged[merged.length - 1] = { ...prev, text: `${prev.text}\n\n${block.text}` };
    } else {
      merged.push(block);
    }
  }
  return merged;
}

/**
 * Initial blockResponseCounts for a fresh smartMessage doc: every
 * response-capturing block starts fully pending; countable blocks get an
 * empty byValue distribution.
 *
 * @param {Array} body
 * @param {number} recipientCount
 * @returns {Object} { [blockId]: { responded, pending, byValue? } }
 */
export function initBlockResponseCounts(body, recipientCount) {
  const counts = {};
  for (const block of getResponseBlocks(body)) {
    counts[block.id] = {
      responded: 0,
      pending: recipientCount,
      ...(COUNTABLE_BLOCK_TYPES.includes(block.type) ? { byValue: {} } : {}),
    };
  }
  return counts;
}

/**
 * Recipient status from their responses map.
 * complete = every required block answered; partial = some response exists.
 *
 * @param {Array} body
 * @param {Object} responses - { [blockId]: { value, respondedBy, respondedAt } }
 * @returns {('pending'|'partial'|'complete')}
 */
export function computeRecipientStatus(body, responses) {
  const answered = responses && typeof responses === 'object'
    ? Object.keys(responses).filter((k) => responses[k] != null)
    : [];
  if (answered.length === 0) return 'pending';
  const required = getRequiredBlocks(body);
  const allRequired = required.every((b) => answered.includes(b.id));
  return allRequired ? 'complete' : 'partial';
}

/** Plain-text digest of a Block[] body — for lastMessage summaries, email fallbacks, search. */
export function blocksToPlainText(body) {
  if (!isBlockBody(body)) return typeof body === 'string' ? body : '';
  return body
    .map((block) => {
      switch (block?.type) {
        case BLOCK_TYPES.TEXT: return block.text;
        case BLOCK_TYPES.ATTACHMENT:
          return block.label || (block.files || []).map((f) => f.name).join(', ');
        case BLOCK_TYPES.CALENDAR: return block.event?.title || block.label || '';
        case BLOCK_TYPES.LOCATION: return block.place?.name || block.place?.address || '';
        default: return block?.label || '';
      }
    })
    .filter(Boolean)
    .join('\n');
}

export default {
  BLOCK_TYPES,
  RESPONSE_BLOCK_TYPES,
  REQUIRABLE_BLOCK_TYPES,
  COUNTABLE_BLOCK_TYPES,
  TRANSPORT_FOLLOW_UPS,
  isBlockBody,
  isResponseBlock,
  isRequiredBlock,
  getResponseBlocks,
  getRequiredBlocks,
  validateBlocks,
  mergeAdjacentTextBlocks,
  initBlockResponseCounts,
  computeRecipientStatus,
  blocksToPlainText,
};
